// import { useState, useEffect, useRef, useCallback } from "react";
// import { useNavigate } from "react-router-dom";
// import { Tag, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
// import axios from "axios";
// import ProductsApiService from "../../services/ProductsApiService";
// import { getLanguage } from "../../utils/Helper";
// import styles from "./SaleProducts.module.scss";

// // ─── i18n ────────────────────────────────────────────────────────────────────
// const t = {
//   EN: {
//     tag:          "Sale",
//     title:        "On Sale Now",
//     subtitle:     "Real prices. Real savings.",
//     allCategories:"All",
//     off:          "% OFF",
//     save:         (n) => `Save ${n.toLocaleString("en-EG")} EGP`,
//     soldPct:      (p) => `${p}% sold`,
//     onlyLeft:     (n) => `Only ${n} left`,
//     outOfStock:   "Sold out",
//     viewAll:      (n) => `View all ${n.toLocaleString()} deals →`,
//     noProducts:   "No sale products right now.",
//     loadMore:     "Load more",
//     loading:      "Loading…",
//   },
//   AR: {
//     tag:          "تخفيضات",
//     title:        "عروض الآن",
//     subtitle:     "أسعار حقيقية. توفير حقيقي.",
//     allCategories:"الكل",
//     off:          "% خصم",
//     save:         (n) => `وفّر ${n.toLocaleString("en-EG")} ج`,
//     soldPct:      (p) => `${p}% بيع`,
//     onlyLeft:     (n) => `${n} فقط متبقية`,
//     outOfStock:   "نفذ المخزون",
//     viewAll:      (n) => `عرض كل ${n.toLocaleString()} عرض ←`,
//     noProducts:   "لا توجد عروض حالياً.",
//     loadMore:     "تحميل المزيد",
//     loading:      "جارٍ التحميل…",
//   },
// };

// // ─── helpers ─────────────────────────────────────────────────────────────────
// const localName = (obj, lang, enKey = "nameEn", arKey = "nameAr") =>
//   (lang === "AR" ? obj?.[arKey] : obj?.[enKey]) || obj?.[enKey] || "";

// const slug = (s) =>
//   s?.toString().toLowerCase().trim().split(" ").join("-") ?? "";

// const stockPct = (product) => {
//   const total = product.totalQuantity ?? 0;
//   const avail = product.availableQuantity ?? 0;
//   if (!total) return 0;
//   return Math.min(100, Math.round(((total - avail) / total) * 100));
// };

// const barColor = (pct) =>
//   pct >= 80 ? "#e63946" :
//   pct >= 50 ? "#f4a91a" :
//               "#2a7a4b";

// // ─── Skeleton ─────────────────────────────────────────────────────────────────
// const SkeletonRow = () => (
//   <div className={styles.skeletonRow}>
//     <div className={styles.skeletonImg} />
//     <div className={styles.skeletonBody}>
//       <div className={styles.skeletonLine} style={{ width: "30%", height: 10 }} />
//       <div className={styles.skeletonLine} style={{ width: "75%", height: 15 }} />
//       <div className={styles.skeletonLine} style={{ width: "50%", height: 12 }} />
//       <div className={styles.skeletonLine} style={{ width: "100%", height: 5, marginTop: 10 }} />
//     </div>
//   </div>
// );

// // ─── Single sale card — image LEFT, info RIGHT ────────────────────────────────
// const SaleCard = ({ product, lang, style }) => {
//   const [imgError, setImgError] = useState(false);
//   const navigate = useNavigate();
//   const i18n     = t[lang] ?? t.EN;
//   const isRtl    = lang === "AR";

//   const savings  = product.originalPrice && product.discountPrice
//     ? Math.round(product.originalPrice - product.discountPrice)
//     : 0;
//   const pct      = stockPct(product);
//   const avail    = product.availableQuantity ?? 0;
//   const isLow    = avail > 0 && avail <= 5;
//   const isOos    = avail === 0;
//   const color    = barColor(pct);

//   const goTo = () => {
//     const path = `/collection/${slug(product.collectionNameEn)}/${slug(product.categoryNameEn)}/${slug(product.subCategoryNameEn)}/${product.id}`;
//     navigate(path);
//   };

//   return (
//     <article
//       className={`${styles.card} ${isOos ? styles.cardOos : ""}`}
//       style={style}
//       dir={isRtl ? "rtl" : "ltr"}
//       onClick={goTo}
//     >
//       {/* ── Image — left panel ── */}
//       <div className={styles.imgPanel}>
//         {!imgError && product.primaryImageUrl ? (
//           <img
//             src={product.primaryImageUrl}
//             alt={lang === "AR" ? product.nameAr : product.nameEn}
//             className={styles.productImg}
//             loading="lazy"
//             onError={() => setImgError(true)}
//           />
//         ) : (
//           <div className={styles.imgPlaceholder}>🛍️</div>
//         )}

//         {/* Big discount circle */}
//         {product.discountPct > 0 && (
//           <div className={styles.discCircle}>
//             <span className={styles.discPct}>{product.discountPct}</span>
//             <span className={styles.discOff}>{i18n.off}</span>
//           </div>
//         )}

//         {/* OOS overlay */}
//         {isOos && (
//           <div className={styles.oosOverlay}>{i18n.outOfStock}</div>
//         )}
//       </div>

//       {/* ── Info — right panel ── */}
//       <div className={styles.infoPanel}>
//         {/* Brand */}
//         <div className={styles.brandRow}>
//           {product.brandImageUrl && (
//             <img
//               src={product.brandImageUrl}
//               alt={lang === "AR" ? product.brandNameAr : product.brandNameEn}
//               className={styles.brandLogo}
//               onError={(e) => { e.currentTarget.style.display = "none"; }}
//             />
//           )}
//           <span className={styles.brandName}>
//             {lang === "AR" ? product.brandNameAr : product.brandNameEn}
//           </span>
//         </div>

//         {/* Name */}
//         <h3 className={styles.productName}>
//           {lang === "AR" ? product.nameAr : product.nameEn}
//         </h3>

//         {/* Status badges */}
//         <div className={styles.badgeRow}>
//           {product.isNew      && <span className={`${styles.badge} ${styles.badgeNew}`}>NEW</span>}
//           {product.isTrend    && <span className={`${styles.badge} ${styles.badgeTrend}`}>TREND</span>}
//           {product.isTopSeller&& <span className={`${styles.badge} ${styles.badgeTop}`}>TOP</span>}
//         </div>

//         {/* Prices */}
//         <div className={styles.priceBlock}>
//           <span className={styles.priceNow}>
//             {product.discountPrice?.toLocaleString("en-EG")} EGP
//           </span>
//           <span className={styles.priceWas}>
//             {product.originalPrice?.toLocaleString("en-EG")} EGP
//           </span>
//         </div>

//         {/* Savings chip */}
//         {savings > 0 && (
//           <span className={styles.savingsChip}>{i18n.save(savings)}</span>
//         )}

//         {/* Stock bar */}
//         {!isOos && (
//           <div className={styles.stockWrap}>
//             <div className={styles.stockBar}>
//               <div
//                 className={styles.stockFill}
//                 style={{ width: `${pct}%`, background: color }}
//               />
//             </div>
//             <div className={styles.stockLabels}>
//               <span style={{ color }}>{i18n.soldPct(pct)}</span>
//               {isLow && (
//                 <span className={styles.lowLabel}>⚡ {i18n.onlyLeft(avail)}</span>
//               )}
//             </div>
//           </div>
//         )}

//         {/* CTA arrow */}
//         <div className={styles.ctaArrow}>
//           <ArrowRight size={16} />
//         </div>
//       </div>
//     </article>
//   );
// };

// // ─── Main ─────────────────────────────────────────────────────────────────────
// const SaleProducts = ({ pageSize = 10 }) => {
//   const lang     = getLanguage();
//   const i18n     = t[lang] ?? t.EN;
//   const isRtl    = lang === "AR";
//   const navigate = useNavigate();

//   const [collections,      setCollections]      = useState([]);
//   const [categories,       setCategories]        = useState([]);
//   const [products,         setProducts]          = useState([]);
//   const [totalCount,       setTotalCount]        = useState(0);
//   const [hasNext,          setHasNext]           = useState(false);
//   const [currentPage,      setCurrentPage]       = useState(0);

//   const [activeCollection, setActiveCollection]  = useState(null);
//   const [activeCategory,   setActiveCategory]    = useState(null);

//   const [loadingInit,      setLoadingInit]        = useState(true);
//   const [loadingProducts,  setLoadingProducts]   = useState(false);
//   const [loadingMore,      setLoadingMore]        = useState(false);

//   // ── tabs scroll ──────────────────────────────────────────────────────────
//   const [canScrollLeft,  setCanScrollLeft]  = useState(false);
//   const [canScrollRight, setCanScrollRight] = useState(false);
//   const tabsRef  = useRef(null);
//   const abortRef = useRef(null);

//   const checkScroll = () => {
//     const el = tabsRef.current;
//     if (!el) return;
//     setCanScrollLeft(el.scrollLeft > 4);
//     setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
//   };
//   const scrollTabs = (dir) =>
//     tabsRef.current?.scrollBy({ left: dir === "right" ? 200 : -200, behavior: "smooth" });

//   useEffect(() => {
//     const el = tabsRef.current;
//     if (!el) return;
//     checkScroll();
//     el.addEventListener("scroll", checkScroll, { passive: true });
//     const ro = new ResizeObserver(checkScroll);
//     ro.observe(el);
//     return () => { el.removeEventListener("scroll", checkScroll); ro.disconnect(); };
//   }, [collections]);

//   // ── 1. boot ───────────────────────────────────────────────────────────────
//   useEffect(() => {
//     (async () => {
//       try {
//         setLoadingInit(true);
//         const data = await ProductsApiService.getFilters();
//         const cols = data.collections ?? [];
//         setCollections(cols);
//         if (cols.length) setActiveCollection(cols[0].id);
//       } catch (err) {
//         console.error("SaleProducts init:", err);
//       } finally {
//         setLoadingInit(false);
//       }
//     })();
//   }, []);

//   // ── 2. categories per collection ──────────────────────────────────────────
//   useEffect(() => {
//     if (!activeCollection) return;
//     setActiveCategory(null);
//     setCurrentPage(0);
//     ProductsApiService.getCategoriesByCollection(activeCollection)
//       .then((cats) => setCategories(cats ?? []))
//       .catch(console.error);
//   }, [activeCollection]);

//   // ── 3. fetch sale products (page 0 on any filter change) ─────────────────
//   const fetchPage = useCallback(async (page, append = false) => {
//     if (abortRef.current) abortRef.current.abort();
//     abortRef.current = new AbortController();

//     if (append) setLoadingMore(true);
//     else        setLoadingProducts(true);

//     try {
//       // uses the NEW endpoint: GET /api/products/sale
//       const params = new URLSearchParams({
//         page,
//         limit: pageSize,
//         ...(activeCollection && { collectionId: activeCollection }),
//         ...(activeCategory   && { categoryIds:  activeCategory   }),
//       });

//       const res  = await ProductsApiService.getSaleProducts(params);
//       const list = res.products ?? [];

//       setProducts((prev) => (append ? [...prev, ...list] : list));
//       setTotalCount(res.totalCount ?? 0);
//       setHasNext(res.hasNext ?? false);
//     } catch (err) {
//       if (!axios.isCancel(err)) console.error("SaleProducts fetch:", err);
//     } finally {
//       setLoadingProducts(false);
//       setLoadingMore(false);
//     }
//   }, [activeCollection, activeCategory, pageSize]);

//   // reset + re-fetch when filters change
//   useEffect(() => {
//     if (!activeCollection) return;
//     setCurrentPage(0);
//     fetchPage(0, false);
//   }, [activeCollection, activeCategory]);

//   const handleLoadMore = () => {
//     const next = currentPage + 1;
//     setCurrentPage(next);
//     fetchPage(next, true);
//   };

//   const handleViewAll = () => {
//     const col = collections.find((c) => c.id === activeCollection);
//     if (col?.nameEn) navigate(`/collection/${slug(col.nameEn)}`);
//   };

//   // ── skeletons on boot ─────────────────────────────────────────────────────
//   if (loadingInit) {
//     return (
//       <section className={styles.section} dir={isRtl ? "rtl" : "ltr"}>
//         <div className={styles.inner}>
//           <div className={styles.skeletonHeader} />
//           <div className={styles.twoCol}>
//             {Array.from({ length: pageSize }).map((_, i) => <SkeletonRow key={i} />)}
//           </div>
//         </div>
//       </section>
//     );
//   }

//   // ── render ────────────────────────────────────────────────────────────────
//   return (
//     <section className={styles.section} dir={isRtl ? "rtl" : "ltr"}>
//       <div className={styles.inner}>

//         {/* ── Header ── */}
//         <div className={styles.header}>
//           <div className={styles.headerLeft}>
//             <div className={styles.tagRow}>
//               <Tag size={13} className={styles.tagIcon} />
//               <span className={styles.tagTxt}>{i18n.tag}</span>
//             </div>
//             <h2 className={styles.title}>{i18n.title}</h2>
//             <p className={styles.subtitle}>{i18n.subtitle}</p>
//           </div>

//           {!loadingProducts && totalCount > 0 && (
//             <button className={styles.viewAllBtn} onClick={handleViewAll}>
//               {i18n.viewAll(totalCount)}
//             </button>
//           )}
//         </div>

//         {/* ── Collection tabs ── */}
//         {collections.length > 0 && (
//           <div className={styles.tabsScroll}>
//             <button
//               className={`${styles.tabArrow} ${canScrollLeft ? styles.tabArrowOn : ""}`}
//               onClick={() => scrollTabs("left")}
//               aria-label="scroll left"
//             >
//               <ChevronLeft size={15} />
//             </button>

//             <div className={`${styles.tabFade} ${styles.tabFadeL} ${canScrollLeft ? styles.tabFadeOn : ""}`} />

//             <div
//               className={styles.tabsRow}
//               ref={tabsRef}
//               role="tablist"
//               onScroll={checkScroll}
//             >
//               {collections.map((col) => (
//                 <button
//                   key={col.id}
//                   role="tab"
//                   aria-selected={activeCollection === col.id}
//                   className={`${styles.tab} ${activeCollection === col.id ? styles.tabOn : ""}`}
//                   onClick={() => setActiveCollection(col.id)}
//                 >
//                   {localName(col, lang)}
//                 </button>
//               ))}
//             </div>

//             <div className={`${styles.tabFade} ${styles.tabFadeR} ${canScrollRight ? styles.tabFadeOn : ""}`} />

//             <button
//               className={`${styles.tabArrow} ${canScrollRight ? styles.tabArrowOn : ""}`}
//               onClick={() => scrollTabs("right")}
//               aria-label="scroll right"
//             >
//               <ChevronRight size={15} />
//             </button>
//           </div>
//         )}

//         {/* ── Category pills ── */}
//         {categories.length > 0 && (
//           <div className={styles.pillsRow} role="group">
//             <button
//               className={`${styles.pill} ${!activeCategory ? styles.pillActive : ""}`}
//               onClick={() => setActiveCategory(null)}
//             >
//               {i18n.allCategories}
//             </button>
//             {categories.map((cat) => (
//               <button
//                 key={cat.id}
//                 className={`${styles.pill} ${activeCategory === cat.id ? styles.pillActive : ""}`}
//                 onClick={() => setActiveCategory((p) => (p === cat.id ? null : cat.id))}
//               >
//                 {localName(cat, lang)}
//               </button>
//             ))}
//           </div>
//         )}

//         {/* ── Cards — 2-column grid of horizontal rows ── */}
//         {loadingProducts ? (
//           <div className={styles.twoCol}>
//             {Array.from({ length: pageSize }).map((_, i) => <SkeletonRow key={i} />)}
//           </div>
//         ) : products.length === 0 ? (
//           <div className={styles.emptyState}>
//             <Tag size={32} className={styles.emptyIcon} />
//             <p>{i18n.noProducts}</p>
//           </div>
//         ) : (
//           <>
//             <div className={styles.twoCol}>
//               {products.map((product, i) => (
//                 <SaleCard
//                   key={product.id}
//                   product={product}
//                   lang={lang}
//                   style={{ animationDelay: `${(i % pageSize) * 0.04}s` }}
//                 />
//               ))}
//             </div>

//             {/* ── Load More ── */}
//             {hasNext && (
//               <div className={styles.loadMoreRow}>
//                 <button
//                   className={styles.loadMoreBtn}
//                   onClick={handleLoadMore}
//                   disabled={loadingMore}
//                 >
//                   {loadingMore ? i18n.loading : i18n.loadMore}
//                   {!loadingMore && <ChevronRight size={16} />}
//                 </button>
//               </div>
//             )}
//           </>
//         )}

//       </div>
//     </section>
//   );
// };

// export default SaleProducts;


import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Tag, ChevronRight, ArrowRight } from "lucide-react";
import axios from "axios";
import ProductsApiService from "../../services/ProductsApiService";
import { getLanguage } from "../../utils/Helper";
import styles from "./SaleProducts.module.scss";

// ─── i18n ────────────────────────────────────────────────────────────────────
const t = {
  EN: {
    tag:        "Sale",
    title:      "On Sale Now",
    subtitle:   "Real prices. Real savings.",
    off:        "% OFF",
    save:       (n) => `Save ${n.toLocaleString("en-EG")} EGP`,
    // viewAll:    (n) => `View all ${n.toLocaleString()} deals →`,
    noProducts: "No sale products right now.",
    loadMore:   "Load more",
    loading:    "Loading…",
  },
  AR: {
    tag:        "تخفيضات",
    title:      "عروض الآن",
    subtitle:   "أسعار حقيقية. توفير حقيقي.",
    off:        "% خصم",
    save:       (n) => `وفّر ${n.toLocaleString("en-EG")} ج`,
    // viewAll:    (n) => `عرض كل ${n.toLocaleString()} خصم ←`,
    noProducts: "لا توجد عروض حالياً.",
    loadMore:   "تحميل المزيد",
    loading:    "جارٍ التحميل…",
  },
};

// ─── helpers ─────────────────────────────────────────────────────────────────
const slug = (s) =>
  s?.toString().toLowerCase().trim().split(" ").join("-") ?? "";

// ─── Skeleton ─────────────────────────────────────────────────────────────────
const SkeletonRow = () => (
  <div className={styles.skeletonRow}>
    <div className={styles.skeletonImg} />
    <div className={styles.skeletonBody}>
      <div className={styles.skeletonLine} style={{ width: "30%", height: 10 }} />
      <div className={styles.skeletonLine} style={{ width: "75%", height: 15 }} />
      <div className={styles.skeletonLine} style={{ width: "50%", height: 12 }} />
    </div>
  </div>
);

// ─── SaleCard — image LEFT, info RIGHT ───────────────────────────────────────
const SaleCard = ({ product, lang, style }) => {
  const [imgError, setImgError] = useState(false);
  const navigate = useNavigate();
  const i18n     = t[lang] ?? t.EN;
  const isRtl    = lang === "AR";

  const savings = product.originalPrice && product.discountPrice
    ? Math.round(product.originalPrice - product.discountPrice)
    : 0;

  const goTo = () => {
    const path = `/collection/${slug(product.collectionNameEn)}/${slug(product.categoryNameEn)}/${slug(product.subCategoryNameEn)}/${product.id}`;
    navigate(path);
  };

  return (
    <article
      className={styles.card}
      style={style}
      dir={isRtl ? "rtl" : "ltr"}
      onClick={goTo}
    >
      {/* ── Image — left panel ── */}
      <div className={styles.imgPanel}>
        {!imgError && product.primaryImageUrl ? (
          <img
            src={product.primaryImageUrl}
            alt={lang === "AR" ? product.nameAr : product.nameEn}
            className={styles.productImg}
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className={styles.imgPlaceholder}>🛍️</div>
        )}

        {/* Discount circle badge */}
        {product.discountPct > 0 && (
          <div className={styles.discCircle}>
            <span className={styles.discPct}>{product.discountPct}</span>
            <span className={styles.discOff}>{i18n.off}</span>
          </div>
        )}
      </div>

      {/* ── Info — right panel ── */}
      <div className={styles.infoPanel}>
        {/* Brand */}
        <div className={styles.brandRow}>
          {product.brandImageUrl && (
            <img
              src={product.brandImageUrl}
              alt={lang === "AR" ? product.brandNameAr : product.brandNameEn}
              className={styles.brandLogo}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
          <span className={styles.brandName}>
            {lang === "AR" ? product.brandNameAr : product.brandNameEn}
          </span>
        </div>

        {/* Product name */}
        <h3 className={styles.productName}>
          {lang === "AR" ? product.nameAr : product.nameEn}
        </h3>

        {/* Status badges */}
        <div className={styles.badgeRow}>
          {product.isNew       && <span className={`${styles.badge} ${styles.badgeNew}`}>NEW</span>}
          {product.isTrend     && <span className={`${styles.badge} ${styles.badgeTrend}`}>TREND</span>}
          {product.isTopSeller && <span className={`${styles.badge} ${styles.badgeTop}`}>TOP</span>}
        </div>

        {/* Prices */}
        <div className={styles.priceBlock}>
          <span className={styles.priceNow}>
            {product.discountPrice?.toLocaleString("en-EG")} EGP
          </span>
          <span className={styles.priceWas}>
            {product.originalPrice?.toLocaleString("en-EG")} EGP
          </span>
        </div>

        {/* Savings chip */}
        {savings > 0 && (
          <span className={styles.savingsChip}>{i18n.save(savings)}</span>
        )}

        {/* CTA arrow */}
        <div className={styles.ctaArrow}>
          <ArrowRight size={16} />
        </div>
      </div>
    </article>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const SaleProducts = ({ pageSize = 10 }) => {
  const lang     = getLanguage();
  const i18n     = t[lang] ?? t.EN;
  const isRtl    = lang === "AR";

  const [products,        setProducts]       = useState([]);
  const [totalCount,      setTotalCount]     = useState(0);
  const [hasNext,         setHasNext]        = useState(false);
  const [currentPage,     setCurrentPage]    = useState(0);
  const [loadingInit,     setLoadingInit]    = useState(true);
  const [loadingProducts, setLoadingProducts]= useState(false);
  const [loadingMore,     setLoadingMore]    = useState(false);

  const abortRef = useRef(null);

  // ── fetch ─────────────────────────────────────────────────────────────────
  const fetchPage = useCallback(async (page, append = false) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    if (append) setLoadingMore(true);
    else        setLoadingProducts(true);

    try {
      const params = new URLSearchParams({ page, limit: pageSize });
      const res    = await ProductsApiService.getSaleProducts(params);
      const list   = res.products ?? [];

      setProducts((prev) => (append ? [...prev, ...list] : list));
      setTotalCount(res.totalCount ?? 0);
      setHasNext(res.hasNext ?? false);
    } catch (err) {
      if (!axios.isCancel(err)) console.error("SaleProducts fetch:", err);
    } finally {
      setLoadingProducts(false);
      setLoadingMore(false);
      setLoadingInit(false);
    }
  }, [pageSize]);

  // ── boot: fetch page 0 once ───────────────────────────────────────────────
  useEffect(() => {
    fetchPage(0, false);
  }, []);

  const handleLoadMore = () => {
    const next = currentPage + 1;
    setCurrentPage(next);
    fetchPage(next, true);
  };

  // ── boot skeleton ─────────────────────────────────────────────────────────
  if (loadingInit) {
    return (
      <section className={styles.section} dir={isRtl ? "rtl" : "ltr"}>
        <div className={styles.inner}>
          <div className={styles.skeletonHeader} />
          <div className={styles.twoCol}>
            {Array.from({ length: pageSize }).map((_, i) => <SkeletonRow key={i} />)}
          </div>
        </div>
      </section>
    );
  }

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <section className={styles.section} dir={isRtl ? "rtl" : "ltr"}>
      <div className={styles.inner}>

        {/* ── Header ── */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.tagRow}>
              <Tag size={13} className={styles.tagIcon} />
              <span className={styles.tagTxt}>{i18n.tag}</span>
            </div>
            <h2 className={styles.title}>{i18n.title}</h2>
            <p className={styles.subtitle}>{i18n.subtitle}</p>
          </div>

          {/* {totalCount > 0 && (
            <span className={styles.totalBadge}>
              {i18n.viewAll(totalCount)}
            </span>
          )} */}
        </div>

        {/* ── Cards ── */}
        {loadingProducts ? (
          <div className={styles.twoCol}>
            {Array.from({ length: pageSize }).map((_, i) => <SkeletonRow key={i} />)}
          </div>
        ) : products.length === 0 ? (
          <div className={styles.emptyState}>
            <Tag size={32} className={styles.emptyIcon} />
            <p>{i18n.noProducts}</p>
          </div>
        ) : (
          <>
            <div className={styles.twoCol}>
              {products.map((product, i) => (
                <SaleCard
                  key={product.id}
                  product={product}
                  lang={lang}
                  style={{ animationDelay: `${(i % pageSize) * 0.04}s` }}
                />
              ))}
            </div>

            {/* ── Load More ── */}
            {hasNext && (
              <div className={styles.loadMoreRow}>
                <button
                  className={styles.loadMoreBtn}
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? i18n.loading : i18n.loadMore}
                  {!loadingMore && <ChevronRight size={16} />}
                </button>
              </div>
            )}
          </>
        )}

      </div>
    </section>
  );
};

export default SaleProducts;